"use client";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getLanguageFromPath,
  removeLanguageFromPath,
  addLanguageToPath,
  type SupportedLanguage,
} from "@/lib/language";

interface LanguageWrapperProps {
  children: React.ReactNode;
}

export function LanguageWrapper({ children }: LanguageWrapperProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get language from current URL
    const detectedLang = getLanguageFromPath(location.pathname);

    // Initialize i18n with the detected language
    if (i18n.language !== detectedLang) {
      i18n.changeLanguage(detectedLang).then(() => {
        setIsInitialized(true);
      });
    } else {
      setIsInitialized(true);
    }
  }, [location.pathname, i18n]);

  // Handle language changes from i18n and update URL accordingly
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      const currentLang = getLanguageFromPath(location.pathname);

      if (lng !== currentLang) {
        // Remove current language prefix and add new one
        const pathWithoutLang = removeLanguageFromPath(location.pathname);
        const newPath = addLanguageToPath(pathWithoutLang, lng as SupportedLanguage);

        // Navigate to the new path with language prefix
        navigate(newPath + location.search + location.hash, { replace: true });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [location, navigate, i18n]);

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
}